import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../shared/AuthContext';
import { usePermissions } from '../shared/permissions';

/**
 * Route wrapper that requires a specific Django model permission.
 * Redirects to /login if not authenticated.
 * Renders a 403 Forbidden page if authenticated but lacking the permission.
 *
 * Usage:
 *   <PermRoute perm="projects.view_project">
 *     <Projects />
 *   </PermRoute>
 */
function PermRoute({ perm, children }) {
  const { isAuthenticated, loading } = useAuth();
  const { hasPerm } = usePermissions();
  const { t } = useTranslation();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('components.permRoute.loading')}</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!hasPerm(perm)) {
    return <Forbidden />;
  }

  return children;
}

/**
 * Inline 403 page rendered when the user lacks the required permission.
 * Intentionally kept simple and reusable.
 */
export function Forbidden() {
  const { t } = useTranslation();
  return (
    <div className="page-wrapper">
      <div className="page-body">
        <div className="container-xl">
          <div className="empty">
            <div className="empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
              </svg>
            </div>
            <p className="empty-title">{t('components.permRoute.forbidden')}</p>
            <p className="empty-subtitle text-muted">
              {t('components.permRoute.forbiddenSubtitle')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PermRoute;
